import { defineStore } from 'pinia'

// Port del root Vuex store (state.js + getters.js + actions.js + mutations.js) → Pinia.
// Stessi nomi di state/getters/actions per minimizzare il diff nei componenti.
// In Pinia le mutations spariscono: le actions assegnano direttamente lo state.
//
// NOTA: il getter `route` (ex vuex-router-sync) è rimosso. In Vue Router 4 si usa
// `useRoute()` (composable) o `this.$route` (Options API).
export const useRootStore = defineStore('root', {
  state: () => ({
    ready: false,
    isLoading: false,
    sideMenu: [],
    menuOpened: false,
    sectionCode: null,
    user: null,
    // true durante il logout: il beforeEnter di /login NON deve rimbalzare l'utente
    // (ancora valorizzato) nella sua app prima che lo stato venga azzerato.
    loggingOut: false,
    lastCustomer: null,
    detail: { title: null, back: null, payload: null },
    navigationTitle: '',
    hasNavigationTitleLink: false,
    cart: { total: 0, count: 0, documentId: '' },
    hasList: false,
    hasAdvancedSearch: false,
    searchText: null,
    selectedNav: '',
    dashboard: {
      analysisDate: new Date(),
      commissionDate: new Date(),
      commissionGoal: 0,
    },
    documentHead: '',
    showNavbar: true,
    packageVersion: import.meta.env.VITE_PACKAGE_VERSION || '0',
    agent: null,
    warehouseCustomerSelected: null,
    lastProduct: null,
    lastCartPath: null,
    productPath: null,
  }),

  getters: {
    // stessi nomi dei getter Vuex (alcuni differiscono dalla chiave di state)
    isReady: (s) => s.ready,
    // isLoading, user, sideMenu, menuOpened, ... coincidono con lo state:
    // in Pinia sono già accessibili come store.xxx, ma li ridefiniamo dove il
    // nome getter differiva dallo state per compatibilità con mapState(useRootStore, [...]).
    appVersion: (s) => s.packageVersion,
  },

  actions: {
    setReady() {
      this.ready = true
    },
    setIsLoading(isLoading) {
      this.isLoading = isLoading
    },
    setUser(user) {
      this.user = user
    },
    setLoggingOut(value) {
      this.loggingOut = value
    },
    setSideMenu(sideMenu) {
      this.sideMenu = sideMenu
    },
    setMenuOpened(menuOpened) {
      this.menuOpened = menuOpened
    },
    setSectionCode(sectionCode) {
      this.sectionCode = sectionCode
    },
    setDetail(detail) {
      this.detail = detail
    },
    setNavigationTitle(title) {
      this.navigationTitle = title
    },
    setHasNavigationTitleLink(state) {
      this.hasNavigationTitleLink = state
    },
    setCart(cart) {
      this.cart = cart
    },
    setSearchText(query) {
      this.searchText = query
    },
    setHasList(hasList) {
      this.hasList = hasList
    },
    setHasAdvancedSearch(hasAdvancedSearch) {
      this.hasAdvancedSearch = hasAdvancedSearch
    },
    setLastCustomer(customer) {
      this.lastCustomer = customer
    },
    setSelectedNav(selectedNav) {
      this.selectedNav = selectedNav
    },
    setDashboardAnalysisDate(analysisDate) {
      this.dashboard.analysisDate = analysisDate
    },
    setNavbarShow(showNavbar) {
      this.showNavbar = showNavbar
    },
    setDashboardAgentCommissionDate(commissionDate) {
      this.dashboard.commissionDate = commissionDate
    },
    setDashboardAgentCommissionGoal(commissionGoal) {
      this.dashboard.commissionGoal = commissionGoal
    },
    setDocumentHead(documentHead) {
      this.documentHead = documentHead
    },
    setAgent(agent) {
      this.agent = agent
    },
    setWarehouseCustomerSelected(warehouseCustomerSelected) {
      this.warehouseCustomerSelected = warehouseCustomerSelected
    },
    setLastProduct(product) {
      this.lastProduct = product
    },
    setLastCartPath(cartPath) {
      this.lastCartPath = cartPath
    },
    setProductPath(productPath) {
      this.productPath = productPath
    },
  },
})
